import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  Stack,
  alpha,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  Notifications,
  Timeline,
  Assessment,
  ShowChart,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SummarySection from './SummarySection';
import RiskHorizonsSection from './RiskHorizonsSection';
import AdditionalMetricsSection from './AdditionalMetricsSection';
import SignalsSection from './SignalsSection';
import ChartsSection from './ChartsSection';
import TimelineSection from './TimelineSection';
import AlertsSection from './AlertsSection';
import { getPhaseColor, getRiskColor } from './utils';

const hasMetricValue = (value) => value !== null && value !== undefined;

const formatSignedPercent = (value, t) => {
  if (!hasMetricValue(value)) return t('downtrend.riskLabels.na');
  const prefix = value >= 0 ? '+' : '';
  return `${prefix}${value.toFixed(2)}%`;
};

const formatPercent = (value, t) => {
  if (!hasMetricValue(value)) return t('downtrend.riskLabels.na');
  return `${value.toFixed(2)}%`;
};

const formatFixed = (value, digits, t) => {
  if (!hasMetricValue(value)) return t('downtrend.riskLabels.na');
  return value.toFixed(digits);
};

function AnalysisContent({
  symbol,
  lookbackDays,
  alertCount,
  activeTab,
  onTabChange,
  overviewNarrative,
  phaseInfo,
  quickFacts,
  riskSnapshot,
  expandedSections,
  toggleSection,
  latestPhase,
  latestResult,
  detectionData,
  chartData,
  alerts,
  alertsLoading,
  onRefreshAlerts,
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  const rootSx = { mb: 4 };

  const heroSx = {
    mb: 2.25,
    p: { xs: 1.5, md: 1.75 },
    borderRadius: 3,
    border: `1px solid ${alpha(phaseInfo.color, 0.12)}`,
    background: `linear-gradient(135deg, ${alpha(phaseInfo.color, 0.08)} 0%, rgba(255,255,255,0.96) 46%, rgba(248,250,252,0.98) 100%)`,
    boxShadow: `0 12px 30px ${alpha(phaseInfo.color, 0.06)}`,
  };

  const heroGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1.6fr) minmax(320px,0.9fr)' },
    gap: 1.75,
    alignItems: 'stretch',
  };

  const heroChipSx = (color, backgroundColor) => ({
    height: 26,
    bgcolor: backgroundColor,
    color,
    fontWeight: 700,
  });

  const eyebrowSx = {
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: phaseInfo.color,
    mb: 0.45,
  };

  const heroTitleSx = {
    fontSize: { xs: '1.32rem', md: '1.7rem' },
    fontWeight: 900,
    letterSpacing: -0.7,
    color: '#0f172a',
    lineHeight: 1.12,
    maxWidth: 680,
  };

  const heroBodySx = {
    mt: 0.75,
    fontSize: '0.88rem',
    color: '#475569',
    lineHeight: 1.65,
    maxWidth: 700,
  };

  const floatingCardSx = {
    borderRadius: 2.5,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    bgcolor: 'rgba(255,255,255,0.86)',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.035)',
  };

  const quickReadCardSx = {
    borderRadius: 2.5,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    background: 'rgba(255,255,255,0.88)',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
  };

  const compactContentSx = { p: 1.5 };

  const sectionEyebrowNeutralSx = {
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#64748b',
    mb: 1,
  };

  const quickFactRowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1.5,
    py: 0.45,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    '&:last-child': { borderBottom: 'none', pb: 0 },
  };

  const tabsWrapSx = {
    mb: 1.75,
    p: 0.45,
    borderRadius: 2.5,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    bgcolor: 'rgba(255,255,255,0.78)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.035)',
  };

  const tabsSx = {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 700,
      minHeight: 46,
      fontSize: '0.88rem',
      px: 2.25,
      py: 1,
      color: 'text.secondary',
      transition: 'all 0.2s ease',
      borderRadius: 2,
      minWidth: 0,
      '& .MuiTab-iconWrapper': {
        mr: 1.5,
        transition: 'color 0.2s ease',
      },
      '&:hover': {
        color: theme.palette.primary.main,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        '& .MuiTab-iconWrapper': {
          color: theme.palette.primary.main,
        },
      },
      '&.Mui-selected': {
        color: theme.palette.primary.main,
        fontWeight: 700,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        '& .MuiTab-iconWrapper': {
          color: theme.palette.primary.main,
        },
      },
    },
    '& .MuiTabs-indicator': {
      height: '100%',
      borderRadius: 16,
      backgroundColor: theme.palette.primary.main,
      opacity: 0.08,
      zIndex: 0,
    },
  };

  const tabPaneSx = { pt: { xs: 0.5, sm: 1 }, width: '100%', boxSizing: 'border-box' };

  const twoColumnPrimarySx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1.4fr) minmax(320px,0.8fr)' },
    gap: 2.25,
    alignItems: 'start',
  };

  const twoColumnSecondarySx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1.2fr) minmax(320px,0.8fr)' },
    gap: 2.25,
    alignItems: 'start',
  };

  const sectionTitleSx = { fontSize: '0.98rem', fontWeight: 800, color: '#0f172a' };
  const sectionBodySx = { fontSize: '0.78rem', color: '#64748b', mt: 0.2 };

  const horizonItemSx = (color) => ({
    p: 1,
    borderRadius: 1.75,
    bgcolor: alpha(color, 0.06),
    border: `1px solid ${alpha(color, 0.12)}`,
  });

  const utilityRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1.5,
    alignItems: 'baseline',
    py: 0.45,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    '&:last-child': { borderBottom: 'none', pb: 0 },
  };

  const utilityMetrics = [
    {
      label: t('downtrend.additionalMetrics.priceChange30d', 'Thay đổi giá (30D)'),
      value: formatSignedPercent(riskSnapshot.priceChange30d, t),
      color: riskSnapshot.priceChange30d >= 0 ? theme.palette.success.main : theme.palette.error.main,
    },
    {
      label: t('downtrend.additionalMetrics.volatility30d', 'Biến động (30D)'),
      value: formatPercent(riskSnapshot.volatility30d, t),
      color: theme.palette.warning.main,
    },
    {
      label: t('downtrend.additionalMetrics.relativeStrength', 'Tỷ số so với VN-Index'),
      value: formatFixed(riskSnapshot.relativeStrength, 4, t),
      color: theme.palette.info.main,
    },
    {
      label: t('downtrend.additionalMetrics.alertsGenerated', 'Cảnh báo đã tạo'),
      value: `${riskSnapshot.alertsGenerated}`,
      color: phaseInfo.color,
    },
  ];

  return (
    <Box sx={rootSx}>
      <Box sx={heroSx}>
        <Box sx={heroGridSx}>
          <Box>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mb: 0.9 }}>
              <Chip label={`${symbol} • ${lookbackDays}D`} size="small" sx={heroChipSx(phaseInfo.color, alpha(phaseInfo.color, 0.12))} />
              <Chip label={`${t('downtrend.tabs.alerts', { count: alertCount })}`} size="small" sx={heroChipSx(theme.palette.warning.dark, alpha(theme.palette.warning.main, 0.12))} />
              <Chip label={`${riskSnapshot.dataPoints} ${t('downtrend.timeline.dataPoints', 'điểm dữ liệu')}`} size="small" sx={heroChipSx(theme.palette.primary.main, alpha(theme.palette.primary.main, 0.08))} />
            </Stack>
            <Typography sx={eyebrowSx}>
              {overviewNarrative?.eyebrow}
            </Typography>
            <Typography sx={heroTitleSx}>
              {overviewNarrative?.title}
            </Typography>
            <Typography sx={heroBodySx}>
              {overviewNarrative?.body}
            </Typography>
          </Box>

          <Card elevation={0} sx={quickReadCardSx}>
            <CardContent sx={compactContentSx}>
              <Typography sx={sectionEyebrowNeutralSx}>
                {t('downtrend.overview.quickRead', 'Đọc nhanh trước khi quyết định')}
              </Typography>
              <Stack spacing={0.7}>
                {quickFacts.map((fact) => (
                  <Box key={fact.label} sx={quickFactRowSx}>
                    <Typography sx={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 600 }}>
                      {fact.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: fact.tone, fontWeight: 800, textAlign: 'right', lineHeight: 1.4 }}>
                      {fact.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={tabsWrapSx}>
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          sx={tabsSx}
        >
          <Tab icon={<Assessment />} label={t('downtrend.tabs.overview')} iconPosition="start" />
          <Tab icon={<ShowChart />} label={t('downtrend.tabs.charts')} iconPosition="start" />
          <Tab icon={<Timeline />} label={t('downtrend.tabs.timeline')} iconPosition="start" />
          <Tab icon={<Notifications />} label={t('downtrend.tabs.alerts', { count: alertCount })} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box sx={tabPaneSx}>
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box sx={twoColumnPrimarySx}>
              <Box>
                <SummarySection
                  latestPhase={latestPhase}
                  latestResult={latestResult}
                  detectionData={detectionData}
                />
              </Box>
              <Card elevation={0} sx={floatingCardSx}>
                <CardContent sx={compactContentSx}>
                  <Typography sx={sectionEyebrowNeutralSx}>
                    {t('downtrend.overview.horizonBoard', 'Bản đồ rủi ro theo khung thời gian')}
                  </Typography>
                  <Stack spacing={0.8}>
                    {riskSnapshot.horizons.map((item) => {
                      const itemPhase = getPhaseColor(item.phase);
                      const itemRiskColor = getRiskColor(item.risk);
                      return (
                        <Box key={item.key} sx={horizonItemSx(itemPhase.color)}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                            <Box>
                              <Typography sx={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>{item.label}</Typography>
                              <Typography sx={{ fontSize: '0.84rem', color: itemPhase.color, fontWeight: 800, mt: 0.15 }}>
                                {t(itemPhase.labelKey)}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.92rem', color: itemRiskColor, fontWeight: 900, fontFamily: 'monospace' }}>
                              {hasMetricValue(item.risk) ? `${item.risk.toFixed(1)}%` : t('downtrend.riskLabels.na')}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box sx={twoColumnSecondarySx}>
              <Box>
                <Box sx={{ mb: 0.9 }}>
                  <Typography sx={sectionTitleSx}>
                    {t('downtrend.overview.signalTitle', 'Điều cần chú ý ngay')}
                  </Typography>
                  <Typography sx={sectionBodySx}>
                    {t('downtrend.overview.signalBody', 'Các tín hiệu dưới đây là nơi nên đọc trước để hiểu vì sao dashboard đang xếp trạng thái hiện tại như vậy.')}
                  </Typography>
                </Box>
                <SignalsSection
                  detectionData={detectionData}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                />
              </Box>

              <Card elevation={0} sx={floatingCardSx}>
                <CardContent sx={compactContentSx}>
                  <Typography sx={sectionEyebrowNeutralSx}>
                    {t('downtrend.overview.utilityBoard', 'Con số nên dùng để hành động')}
                  </Typography>
                  <Stack spacing={0.75}>
                    {utilityMetrics.map((item) => (
                      <Box key={item.label} sx={utilityRowSx}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 600 }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: '0.84rem', color: item.color, fontWeight: 900, fontFamily: 'monospace', textAlign: 'right' }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Box sx={{ mb: 0.9 }}>
                <Typography sx={sectionTitleSx}>
                  {t('downtrend.overview.horizonSection', 'Diễn biến rủi ro theo từng khung nhìn')}
                </Typography>
                <Typography sx={sectionBodySx}>
                  {t('downtrend.overview.horizonSectionBody', 'So sánh nhanh 10, 20 và 60 ngày để thấy áp lực đang tăng nhất ở nhịp ngắn, trung hay dài hạn.')}
                </Typography>
              </Box>
              <RiskHorizonsSection latestResult={latestResult} />
            </Box>

            <Box>
              <Box sx={{ mb: 0.9 }}>
                <Typography sx={sectionTitleSx}>
                  {t('downtrend.overview.metricSection', 'Chỉ số bổ sung để xác nhận bối cảnh')}
                </Typography>
                <Typography sx={sectionBodySx}>
                  {t('downtrend.overview.metricSectionBody', 'Nhóm chỉ số này giúp kiểm tra đà giá, độ nhiễu và sức mạnh tương đối thay vì chỉ nhìn một điểm rủi ro tổng hợp.')}
                </Typography>
              </Box>
              <AdditionalMetricsSection detectionData={detectionData} />
            </Box>
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={tabPaneSx}>
          <ChartsSection chartData={chartData} />
        </Box>
      )}

      {activeTab === 2 && (
        <Box sx={tabPaneSx}>
          <TimelineSection chartData={chartData} />
        </Box>
      )}

      {activeTab === 3 && (
        <Box sx={tabPaneSx}>
          <AlertsSection
            alerts={alerts}
            alertsLoading={alertsLoading}
            alertCount={alertCount}
            onRefresh={onRefreshAlerts}
          />
        </Box>
      )}
    </Box>
  );
}

AnalysisContent.propTypes = {
  symbol: PropTypes.string.isRequired,
  lookbackDays: PropTypes.number.isRequired,
  alertCount: PropTypes.number.isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  overviewNarrative: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
  }),
  phaseInfo: PropTypes.shape({
    color: PropTypes.string.isRequired,
  }).isRequired,
  quickFacts: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    tone: PropTypes.string,
  })).isRequired,
  riskSnapshot: PropTypes.shape({
    horizons: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      phase: PropTypes.string,
      risk: PropTypes.number,
    })).isRequired,
    dataPoints: PropTypes.number.isRequired,
    priceChange30d: PropTypes.number,
    volatility30d: PropTypes.number,
    relativeStrength: PropTypes.number,
    alertsGenerated: PropTypes.number.isRequired,
  }).isRequired,
  expandedSections: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
  latestPhase: PropTypes.string,
  latestResult: PropTypes.object,
  detectionData: PropTypes.object.isRequired,
  chartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  alerts: PropTypes.arrayOf(PropTypes.object).isRequired,
  alertsLoading: PropTypes.bool.isRequired,
  onRefreshAlerts: PropTypes.func.isRequired,
};

export default AnalysisContent;
